import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { CheckCircle2 } from "lucide-react";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import Seo from "@/components/Seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  company: z.string().trim().max(120).optional().or(z.literal("")),
  email: z.string().trim().email("Please enter a valid email").max(255),
  useCase: z.string().min(1, "Please select a use case"),
  message: z.string().trim().min(1, "Message is required").max(1000),
});

const useCaseOptions = [
  "A2P SMS",
  "OTP Authentication",
  "Voice Termination",
  "Transactional Alerts",
  "Promotional SMS",
  "Payment & Fintech",
  "Other",
];

const faqs = [
  {
    q: "How quickly will Yootelco respond to my inquiry?",
    a: "Our team responds to all inquiries within 24 hours. For urgent matters, you can reach us directly at info@yootelco.com or call +44 207 0999469.",
  },
  {
    q: "What information should I prepare before contacting Yootelco?",
    a: "It helps to know your expected monthly message volume, target countries, use case type (OTP, transactional, promotional), and any specific compliance requirements for your market.",
  },
  {
    q: "Can I request a demo or trial before committing?",
    a: "Yes. Contact our team and we will set up a demo tailored to your use case and provide access to our testing environment so you can evaluate delivery performance firsthand.",
  },
  {
    q: "Does Yootelco offer custom pricing for enterprise clients?",
    a: "Yes. We offer volume-based pricing and custom SLA agreements for enterprise clients. Contact our sales team to discuss a package that fits your messaging needs and budget.",
  },
  {
    q: "Which regions does Yootelco support for enterprise clients?",
    a: "Yootelco supports enterprises globally across 200+ countries with direct carrier connections, local compliance expertise, and dedicated account management for key markets.",
  },
];

const fieldClass =
  "h-12 w-full rounded-[8px] border border-[#e5e7eb] bg-white px-4 text-[14px] text-foreground outline-none transition-shadow placeholder:text-muted-foreground focus:border-[#34d4a8] focus:ring-2 focus:ring-[#34d4a8]/20";

const labelClass = "mb-1.5 block text-[12px] font-medium text-[#374151]";

// Formspree endpoint — submissions are delivered to info@yootelco.com.
// Replace the form ID below with the live Formspree form ID once created.
const FORMSPREE_ENDPOINT = "https://formspree.io/f/mbdelepb";

const Contact = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    useCase: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = contactSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((i) => {
        const k = i.path[0] as string;
        if (!fieldErrors[k]) fieldErrors[k] = i.message;
      });
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const [formspreeRes, { error }] = await Promise.all([
        fetch(FORMSPREE_ENDPOINT, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: form.name,
            company: form.company,
            email: form.email,
            use_case: form.useCase,
            message: form.message,
          }),
        }),
        supabase.from("leads").insert({
          name: form.name,
          company: form.company || null,
          email: form.email,
          use_case: form.useCase,
          message: form.message,
        }),
      ]);
      if (!formspreeRes.ok) throw new Error(`Formspree submission failed: ${formspreeRes.status}`);
      if (error) throw error;
      setSubmitted(true);
      setForm({ name: "", company: "", email: "", useCase: "", message: "" });
    } catch (err) {
      toast({
        title: "Couldn't send message",
        description: "Please try again or email info@yootelco.com directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-white">
      <Seo
        title="Contact Yootelco | Get Telecom Rates & Partnership Info"
        description="Get in touch with Yootelco for enterprise messaging rates, wholesale partnerships, or technical support. Talk to our team today."
        path="/contact"
        faqs={faqs.map((f) => ({ question: f.q, answer: f.a }))}
      />
      <SiteHeader />

      <section className="container pt-[120px] pb-10 md:pt-[140px] md:pb-14">
        <div className="mx-auto max-w-[1180px]">
          <h1 className="text-center font-display text-[40px] font-bold tracking-tight text-foreground md:text-[56px]">
            Contact us
          </h1>

          <div className="mt-10 grid items-stretch gap-6 lg:grid-cols-2">
            {/* FORM */}
            {submitted ? (
              <div className="rounded-[20px] border border-border/70 bg-card p-8 text-center shadow-card md:p-10">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h2 className="mt-5 font-display text-[24px] font-bold text-foreground">
                  Thank you!
                </h2>
                <p className="mt-2 text-[15px] text-muted-foreground">
                  We'll get back to you within 24 hours.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 inline-flex h-11 items-center justify-center rounded-[8px] border border-border bg-background px-5 text-[14px] font-semibold text-foreground transition-colors hover:bg-secondary"
                >
                  Send another message
                </button>
              </div>
            ) : (
            <form
              onSubmit={handleSubmit}
              action={FORMSPREE_ENDPOINT}
              method="POST"
              noValidate
              className="flex h-full flex-col"
            >
              <div className="space-y-5">
                <div>
                  <label htmlFor="name" className={labelClass}>
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Name"
                    maxLength={100}
                    className={fieldClass}
                  />
                  {errors.name && <p className="mt-1 text-[12px] text-destructive">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="company" className={labelClass}>
                    Company
                  </label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Company"
                    maxLength={120}
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label htmlFor="email" className={labelClass}>
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email"
                    maxLength={255}
                    className={fieldClass}
                  />
                  {errors.email && <p className="mt-1 text-[12px] text-destructive">{errors.email}</p>}
                </div>

                <div>
                  <label className={labelClass}>Use Case</label>
                  <Select value={form.useCase} onValueChange={(v) => setForm({ ...form, useCase: v })}>
                    <SelectTrigger className="h-12 rounded-[8px] border border-[#e5e7eb] bg-white px-4 text-[14px] focus:ring-2 focus:ring-[#34d4a8]/20">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {useCaseOptions.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" name="use_case" value={form.useCase} />
                  {errors.useCase && <p className="mt-1 text-[12px] text-destructive">{errors.useCase}</p>}
                </div>

                <div>
                  <label htmlFor="message" className={labelClass}>
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Type here..."
                    rows={5}
                    maxLength={1000}
                    className="w-full resize-y rounded-[8px] border border-[#e5e7eb] bg-white px-4 py-3 text-[14px] text-foreground outline-none transition-shadow placeholder:text-muted-foreground focus:border-[#34d4a8] focus:ring-2 focus:ring-[#34d4a8]/20"
                  />
                  {errors.message && <p className="mt-1 text-[12px] text-destructive">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-2 inline-flex h-12 w-full items-center justify-center rounded-[10px] bg-primary text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
            )}

            {/* INFO */}
            <aside
              className="h-full rounded-[20px] bg-[#f8f9fa] p-6 md:p-8"
            >
              <p className="text-[16px] font-medium leading-[1.55] text-foreground">
                Tell us about your messaging requirements and we'll help you find the right solution.
              </p>

              <div className="mt-8 space-y-6 text-[14px] leading-[1.55]">
                <div>
                  <h2 className="font-bold text-foreground">Our Offices</h2>
                  <ul className="mt-1 space-y-3 text-muted-foreground">
                    <li>
                      <span className="font-medium text-foreground">United Kingdom</span><br />
                      103 High Street, Waltham Cross,<br />
                      Hertfordshire EN8 7AN
                    </li>
                    <li>
                      <span className="font-medium text-foreground">Hong Kong</span><br />
                      Unit 406B, Mirror Tower, 61 Mody Road,<br />
                      Tsim Sha Tsui East, Kowloon
                    </li>
                    <li>
                      <span className="font-medium text-foreground">Armenia</span><br />
                      26/4 Saryan Street,<br />
                      0002, Yerevan
                    </li>
                  </ul>
                </div>
                <div>
                  <h2 className="font-bold text-foreground">Email</h2>
                  <a
                    href="mailto:info@yootelco.com"
                    className="mt-1 inline-block text-primary transition-colors hover:text-primary/80"
                  >
                    info@yootelco.com
                  </a>
                </div>
                <div>
                  <h2 className="font-bold text-foreground">Phone</h2>
                  <a
                    href="tel:+442070999469"
                    className="mt-1 inline-block text-primary transition-colors hover:text-primary/80"
                  >
                    +44 207 0999469
                  </a>
                </div>
              </div>
            </aside>
          </div>

          {/* FAQ */}
          <div className="mt-20 md:mt-24">
            <h2 className="text-center font-display text-[28px] font-bold tracking-tight text-foreground md:text-[36px]">
              Frequently Asked Questions
            </h2>

            <div className="mx-auto mt-8 max-w-[920px] rounded-[20px] border border-border/70 bg-card px-2 shadow-card md:px-4">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-border/60">
                    <AccordionTrigger className="px-4 py-5 text-left text-[15px] font-semibold text-foreground hover:no-underline md:text-[16px]">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-5 text-[14px] leading-[1.6] text-muted-foreground">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default Contact;
